import type { Static } from '@sinclair/typebox';
import { Type } from '@sinclair/typebox';

import { MODEL_TYPE_PATTERN } from './constants';

// NEW REFACTORED API TYPES

// Dashboard-optimized item type (minimal fields for list views)
export const DashboardItemSchema = Type.Object({
  id: Type.String({ format: 'uuid' }),
  name: Type.String(),
  thumbnail_url: Type.String(),
  status_id: Type.Number(),
  model_type: Type.Optional(Type.String()),
  is_bookmarked: Type.Boolean(),
  // Add only essential properties that are actually used in dashboard
  visibility: Type.Optional(Type.String()), // Optional for dashboard items
  // Statistics - optional for backwards compatibility
  bookmark_count: Type.Optional(Type.Number()),
  like_count: Type.Optional(Type.Number()),
  view_count_total: Type.Optional(Type.Number()),
  is_liked: Type.Optional(Type.Boolean()), // User's like status
  content_type: Type.Optional(
    Type.Union([Type.Literal('item'), Type.Literal('scene')])
  ),
  owner: Type.Optional(
    Type.Object({
      id: Type.String({ format: 'uuid' }),
      username: Type.String(),
      avatar: Type.Union([Type.String(), Type.Null()]),
    })
  ),
  // Badge-related fields
  is_embed_enabled: Type.Optional(Type.Boolean()),
  has_product_link: Type.Optional(Type.Boolean()),
  platform: Type.Optional(Type.String()),
  is_copy: Type.Optional(Type.Boolean()),
  is_banned: Type.Optional(Type.Boolean()),
  banned_reason: Type.Optional(Type.Union([Type.String(), Type.Null()])),
  // 0-100, only present while a Meshy generation is actively running
  progress: Type.Optional(Type.Number()),
});
export type DashboardItem = Static<typeof DashboardItemSchema>;

// Polled from GET /items/update to live-update dashboard cards while an item
// is still processing - only the fields that can change mid-processing.
export const ItemUpdateSchema = Type.Object({
  id: Type.String({ format: 'uuid' }),
  status_id: Type.Number(),
  // 0-100, only present while a Meshy generation is actively running
  progress: Type.Optional(Type.Number()),
});
export type ItemUpdate = Static<typeof ItemUpdateSchema>;

export const GetItemsResponseSchema = Type.Object({
  items: Type.Array(DashboardItemSchema),
  totalItems: Type.Number(),
  pageSize: Type.Number(),
});
export type GetItemsResponse = Static<typeof GetItemsResponseSchema>;

// Updated item creation with name parameter
export const CreateItemBodySchema = Type.Object({
  model_type: Type.String({ pattern: MODEL_TYPE_PATTERN }),
  upload_type: Type.String({ pattern: '^(model|image|video)$' }),
  name: Type.Optional(Type.String()),
});
export type CreateItemBody = Static<typeof CreateItemBodySchema>;

export const CreateItemResponseSchema = Type.Object({
  item_id: Type.String({ format: 'uuid' }),
});
export type CreateItemResponse = Static<typeof CreateItemResponseSchema>;

// GET /upload/video/:itemId
export const GetVideoUploadParams = Type.Object({
  itemId: Type.String({ format: 'uuid' }),
});
export type GetVideoUploadParams = Static<typeof GetVideoUploadParams>;

export const GetVideoUploadResponseSchema = Type.Object({
  path: Type.String(),
  url: Type.String(),
});
export type GetVideoUploadResponse = Static<
  typeof GetVideoUploadResponseSchema
>;

// GET /upload/image/:itemId
export const GetImageUploadParams = Type.Object({
  itemId: Type.String({ format: 'uuid' }),
});
export type GetImageUploadParams = Static<typeof GetImageUploadParams>;

export const GetImageUploadResponseSchema = Type.Object({
  path: Type.String(),
  url: Type.String(),
});
export type GetImageUploadResponse = Static<
  typeof GetImageUploadResponseSchema
>;

// GET /upload/image/:itemId/file
export const GetImageFileUploadParams = Type.Object({
  itemId: Type.String({ format: 'uuid' }),
});
export type GetImageFileUploadParams = Static<typeof GetImageFileUploadParams>;

export const GetImageFileUploadQuery = Type.Object({
  filename: Type.String(),
});
export type GetImageFileUploadQuery = Static<typeof GetImageFileUploadQuery>;

export const GetImageFileUploadResponseSchema = Type.Object({
  path: Type.String(),
  url: Type.String(),
});
export type GetImageFileUploadResponse = Static<
  typeof GetImageFileUploadResponseSchema
>;

// POST /processing/video/:itemId
export const VideoProcessingParamsSchema = Type.Object({
  itemId: Type.String({ format: 'uuid' }),
});
export type VideoProcessingParams = Static<typeof VideoProcessingParamsSchema>;

export const VideoProcessingBodySchema = Type.Object({
  duration: Type.Number(),
  width: Type.Number(),
  height: Type.Number(),
  input_bucket_path: Type.String(),
});
export type VideoProcessingBody = Static<typeof VideoProcessingBodySchema>;

// POST /processing/image/:itemId
export const ImageProcessingParamsSchema = Type.Object({
  itemId: Type.String({ format: 'uuid' }),
});
export type ImageProcessingParams = Static<typeof ImageProcessingParamsSchema>;

export const ImageProcessingBodySchema = Type.Object({
  input_bucket_path: Type.String(),
});
export type ImageProcessingBody = Static<typeof ImageProcessingBodySchema>;

// New types for dynamic subscription pricing and topup functionality

export interface SubscriptionPlan {
  id: string;
  name: string;
  plan_type: 'free' | 'starter' | 'growth' | 'pro';
  stw_plan_id: number;
  scan_quota: number;
  link_quota: number;
  annotation_quota: number;
  price: {
    amount: number;
    currency: string;
    formatted: string;
    interval: string;
  };
  prices?: {
    month?: { id: string; amount: number; currency: string; formatted: string };
    year?: { id: string; amount: number; currency: string; formatted: string };
  };
  computed?: {
    yearly_avg_monthly_amount?: number;
    yearly_avg_monthly_formatted?: string | null;
    annual_savings_amount?: number;
    annual_savings_formatted?: string | null;
    yearly_total_formatted?: string | null;
  };
}

export interface TopupPackage {
  id: string;
  name: string;
  credits: number;
  credit_type: 'scan' | 'link';
  price: {
    amount: number;
    currency: string;
    formatted: string;
  };
}

export interface GetSubscriptionPlansResponse {
  data: {
    plans: SubscriptionPlan[];
  };
}

export interface GetTopupPackagesResponse {
  data: {
    packages: TopupPackage[];
  };
}

export interface TopupHistoryRecord {
  id: string;
  user_id: string;
  credits_added: number;
  created_at: string;
}

// TypeBox schemas for validation
export const SubscriptionPlanSchema = Type.Object({
  id: Type.String(),
  name: Type.String(),
  plan_type: Type.Union([
    Type.Literal('free'),
    Type.Literal('starter'),
    Type.Literal('growth'),
    Type.Literal('pro'),
  ]),
  stw_plan_id: Type.Number(),
  scan_quota: Type.Number(),
  link_quota: Type.Number(),
  annotation_quota: Type.Number(),
  price: Type.Object({
    amount: Type.Number(),
    currency: Type.String(),
    formatted: Type.String(),
    interval: Type.String(),
  }),
  prices: Type.Optional(
    Type.Object({
      month: Type.Optional(
        Type.Object({
          id: Type.String(),
          amount: Type.Number(),
          currency: Type.String(),
          formatted: Type.String(),
        })
      ),
      year: Type.Optional(
        Type.Object({
          id: Type.String(),
          amount: Type.Number(),
          currency: Type.String(),
          formatted: Type.String(),
        })
      ),
    })
  ),
  computed: Type.Optional(
    Type.Object({
      yearly_avg_monthly_amount: Type.Optional(Type.Number()),
      yearly_avg_monthly_formatted: Type.Optional(
        Type.Union([Type.String(), Type.Null()])
      ),
      annual_savings_amount: Type.Optional(Type.Number()),
      annual_savings_formatted: Type.Optional(
        Type.Union([Type.String(), Type.Null()])
      ),
      yearly_total_formatted: Type.Optional(
        Type.Union([Type.String(), Type.Null()])
      ),
    })
  ),
});

export const GetSubscriptionPlansResponseSchema = Type.Object({
  data: Type.Object({
    plans: Type.Array(SubscriptionPlanSchema),
  }),
});

export const TopupPackageSchema = Type.Object({
  id: Type.String(),
  name: Type.String(),
  credits: Type.Number(),
  credit_type: Type.Union([Type.Literal('scan'), Type.Literal('link')]),
  price: Type.Object({
    amount: Type.Number(),
    currency: Type.String(),
    formatted: Type.String(),
  }),
});

export const GetTopupPackagesResponseSchema = Type.Object({
  data: Type.Object({
    packages: Type.Array(TopupPackageSchema),
  }),
});

// User Quotas and Credits API Types
export const QuotasDataSchema = Type.Object({
  link_quota: Type.Number(),
  link_credit: Type.Number(),
  scan_quota: Type.Number(),
  scan_credit: Type.Number(),
  topup_scan_credit: Type.Number(),
  total_scan_credit: Type.Number(),
  annotation_quota: Type.Number(),
  embed_quota: Type.Number(),
  embed_credit: Type.Number(),
  storage_quota: Type.Number(),
  storage_used: Type.Number(),
  scene_quota: Type.Number(),
  scene_count: Type.Number(),
});

export const GetQuotasResponseSchema = Type.Object({
  data: Type.Object({
    quotas: QuotasDataSchema,
  }),
});

/**
 * User quotas and credits data structure
 * @interface QuotasData
 * @property {number} link_quota - Total available link quota
 * @property {number} link_credit - Remaining available links
 * @property {number} scan_quota - Total available scan quota from the subscription plan
 * @property {number} scan_credit - Remaining scans from the user's subscription plan
 * @property {number} topup_scan_credit - Remaining scans from top-ups
 * @property {number} total_scan_credit - Total remaining available scans (plan + topup)
 * @property {number} annotation_quota - Total available annotation quota
 * @property {number} embed_quota - Total available embed quota from the subscription plan
 * @property {number} embed_credit - Remaining available embed quota (quota - currently embedded items)
 */
export type QuotasData = Static<typeof QuotasDataSchema>;

/**
 * Response structure for GET /user/quotas endpoint
 */
export type GetQuotasResponse = Static<typeof GetQuotasResponseSchema>;

// ANNOUNCEMENT API TYPES

export const AnnouncementSchema = Type.Object({
  id: Type.String({ format: 'uuid' }),
  content: Type.String(),
  background_color: Type.String(),
  text_color: Type.String(),
  is_active: Type.Boolean(),
  order: Type.Number(),
  is_closable: Type.Boolean(),
});
export type Announcement = Static<typeof AnnouncementSchema>;

export const CreateAnnouncementBodySchema = Type.Object({
  content: Type.String(),
  background_color: Type.Optional(Type.String()),
  text_color: Type.Optional(Type.String()),
  is_active: Type.Optional(Type.Boolean()),
  order: Type.Optional(Type.Number()),
  is_closable: Type.Optional(Type.Boolean()),
});
export type CreateAnnouncementBody = Static<
  typeof CreateAnnouncementBodySchema
>;

export const UpdateAnnouncementBodySchema = Type.Object({
  content: Type.Optional(Type.String()),
  background_color: Type.Optional(Type.String()),
  text_color: Type.Optional(Type.String()),
  is_active: Type.Optional(Type.Boolean()),
  order: Type.Optional(Type.Number()),
  is_closable: Type.Optional(Type.Boolean()),
});
export type UpdateAnnouncementBody = Static<
  typeof UpdateAnnouncementBodySchema
>;

export const GetAnnouncementsResponseSchema = Type.Object({
  data: Type.Array(AnnouncementSchema),
});
export type GetAnnouncementsResponse = Static<
  typeof GetAnnouncementsResponseSchema
>;

// CREDIT USAGE API TYPES

// Embedded items and scenes
export const EmbeddedItemSchema = Type.Object({
  id: Type.String({ format: 'uuid' }),
  name: Type.String(),
  type: Type.Union([Type.Literal('item'), Type.Literal('scene')]),
  embed_enabled_at: Type.Union([Type.String(), Type.Null()]),
  url: Type.String(),
});
export type EmbeddedItem = Static<typeof EmbeddedItemSchema>;

export const GetEmbeddedItemsResponseSchema = Type.Object({
  data: Type.Object({
    items: Type.Array(EmbeddedItemSchema),
    total: Type.Number(),
    page: Type.Number(),
    limit: Type.Number(),
  }),
});
export type GetEmbeddedItemsResponse = Static<
  typeof GetEmbeddedItemsResponseSchema
>;

// Linked items
export const LinkedItemSchema = Type.Object({
  id: Type.String({ format: 'uuid' }),
  name: Type.String(),
  shop_name: Type.String(),
  shop_platform: Type.String(),
  product_id: Type.String(),
  linked_at: Type.String(),
  url: Type.String(),
});
export type LinkedItem = Static<typeof LinkedItemSchema>;

export const GetLinkedItemsResponseSchema = Type.Object({
  data: Type.Object({
    items: Type.Array(LinkedItemSchema),
    total: Type.Number(),
    page: Type.Number(),
    limit: Type.Number(),
  }),
});
export type GetLinkedItemsResponse = Static<
  typeof GetLinkedItemsResponseSchema
>;

// Scan credit history
export const CreditHistoryItemSchema = Type.Object({
  id: Type.String({ format: 'uuid' }),
  amount: Type.Number(),
  credit_type: Type.String(),
  balance_type: Type.String(),
  action: Type.String(),
  reference_type: Type.Union([Type.String(), Type.Null()]),
  reference_id: Type.Union([Type.String({ format: 'uuid' }), Type.Null()]),
  created_at: Type.String(),
});
export type CreditHistoryItem = Static<typeof CreditHistoryItemSchema>;

export const GetScanCreditHistoryResponseSchema = Type.Object({
  data: Type.Object({
    items: Type.Array(CreditHistoryItemSchema),
    total: Type.Number(),
    page: Type.Number(),
    limit: Type.Number(),
  }),
});
export type GetScanCreditHistoryResponse = Static<
  typeof GetScanCreditHistoryResponseSchema
>;

// LIKES & VIEWS API TYPES

// Simplified like response - just the data without wrapper
export type ToggleLikeResponse = {
  is_liked: boolean;
  like_count: number;
};

// Simplified view tracking - just what we need
export type TrackViewResponse = {
  view_count_total: number;
};

// type of things that users can create
// reflects api routes and such
export type EntityType = 'item' | 'scene';
